import { CiEdit } from "react-icons/ci";
import { MdDeleteSweep, MdDownloadDone } from "react-icons/md";
import { IoIosAddCircleOutline } from "react-icons/io";

export const LanguageSwitcher = ({ languages, currentIndex, onSwitch, langLabels }) => (
    <div className="flex items-center gap-2 mb-4">
        {languages.map((lang, idx) => (
            <button
                key={lang}
                type="button"
                onClick={() => onSwitch(idx)}
                className={`px-3 py-1 rounded-full border text-sm ${
                    idx === currentIndex 
                        ? 'bg-blue-600 text-white border-blue-600' 
                        : 'bg-white text-gray-700 border-gray-300'
                }`}
            >
                {langLabels[lang]}
            </button>
        ))}
    </div>
);

export const IndustrySelector = ({ currentLang, register, data }) => {
    const industryOptions = {
        eng: { label: 'Select Industry', data: data?.industry },
        ar: { label: 'الصناعة', data: data?.industryArabicName },
        it: { label: 'Seleziona Industria', data: data?.industryItalianName },
        det: { label: 'Wählen Sie Branche', data: data?.industryGermanName },
        fr: { label: 'Sélectionner Industrie', data: data?.industryFrenchName },
        esp: { label: 'Seleccionar Industria', data: data?.industrySpanishName },
    };

    const config = industryOptions[currentLang];
    const isRtl = currentLang === 'ar';

    return (
        <div className="relative z-0 w-full mb-5 group" dir={`${isRtl ? 'rtl' : ''}`}>
            <select
                {...register("industry")}
                className="p-3 block py-2.5 pr-8 pl-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 focus:outline-none focus:border-blue-600 peer appearance-none"
            >
                <option value="">{config.label}</option>
                {config.data?.map((ind) => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                ))}
            </select>
        </div>
    );
};

export const FormInput = ({ currentLang, fieldName, register, label }) => {
    const isRtl = currentLang === 'ar';
    const placeholder = isRtl ? (fieldName === 'name' ? 'أدخل الاسم' : 'أدخل الوصف') : 
                                (fieldName === 'name' ? 'Enter name' : 'Enter description');

    return (
        <div className="relative z-0 w-full mb-5 group" dir={isRtl ? 'rtl' : 'ltr'}>
            <input
                key={`${fieldName}-${currentLang}`}
                type="text"
                placeholder=" "
                {...register(`${fieldName}.${currentLang}`)}
                className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
            />
            <label className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0]">
                {placeholder}
            </label>
        </div>
    );
};

export const StepInput = ({ currentLang, value, onChange, onAdd }) => {
    const isRtl = currentLang === 'ar';
    const placeholder = isRtl ? 'أدخل عنوان المرحلة' : 'Enter step title';

    return (
        <div className="flex items-center gap-4 mb-6">
            <div className="relative z-0 w-full group" dir={isRtl ? 'rtl' : 'ltr'}>
                <input
                    type="text"
                    value={value}
                    onChange={onChange}
                    placeholder=" "
                    className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                />
                <label className="peer-focus:font-medium absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0]">
                    {placeholder}
                </label>
            </div>
            <button
                type="button"
                onClick={onAdd}
                className="text-green-600"
                title="Add step"
            >
                <IoIosAddCircleOutline className="w-6 h-6" />
            </button>
        </div>
    );
};

export const StepsList = ({ 
    steps, 
    currentLang, 
    langKeys, 
    onStepTitleChange, 
    onStepClick, 
    onRemoveStep,
    onTouchpointTitleChange,
    onDeleteTouchpoint,
    onEditTouchpoint
}) => {
    const isRtl = currentLang === 'ar';

    return (
        <div className="mt-4">
            {steps.eng.map((_, index) => (
                <div key={`step-${currentLang}-${index}`} className="rounded-lg border border-gray-200 bg-gray-50 p-4 mb-4">
                    <div className="flex items-center gap-3">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
                            {index + 1}
                        </span>
                        <input
                            type="text"
                            value={steps[currentLang][index]?.[langKeys[currentLang].titleKey] || ""}
                            onChange={(e) => onStepTitleChange(index, e.target.value)}
                            placeholder={isRtl ? 'عنوان المرحلة' : 'Step title'}
                            className="flex-1 block py-2 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            type="button"
                            onClick={() => onStepClick(index)}
                            className="inline-flex items-center gap-2 rounded-full border border-green-500 px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-600 hover:text-white transition"
                            title={isRtl ? 'إضافة نقطة تلامس' : 'Add touchpoint'}
                        >
                            <IoIosAddCircleOutline className="h-4 w-4" />
                            {isRtl ? 'إضافة' : 'Touchpoint'}
                        </button>
                        <button 
                            type="button" 
                            onClick={() => onRemoveStep(index)} 
                            className="ml-1 rounded-md p-1.5 hover:bg-red-50" 
                            title={isRtl ? 'حذف المرحلة' : 'Remove step'}
                        >
                            <MdDeleteSweep className="w-5 h-5 text-red-600" />
                        </button>
                    </div>
                    <div className="mt-3 space-y-2 pl-9">
                        {steps[currentLang]?.[index]?.[langKeys[currentLang].tpKey]?.map((touchPoint, tpIndex) => (
                            <div key={`tp-${currentLang}-${tpIndex}`} className="flex items-center gap-3">
                                <input
                                    type="text"
                                    value={touchPoint?.[langKeys[currentLang].titleKey] || ""}
                                    onChange={(e) => onTouchpointTitleChange(index, tpIndex, e.target.value)}
                                    placeholder={isRtl ? 'عنوان نقطة التلامس' : 'Touchpoint label'}
                                    className="flex-1 block py-2 px-3 text-sm text-gray-900 bg-white border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                                />
                                <button 
                                    type="button" 
                                    onClick={() => onEditTouchpoint(index, tpIndex)} 
                                    className="rounded-md p-1.5 hover:bg-green-50" 
                                    title={isRtl ? 'تعديل نقطة التلامس' : 'Edit touchpoint'}
                                >
                                    <CiEdit className="w-6 h-6 text-green-600" />
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => onDeleteTouchpoint(index, tpIndex)} 
                                    className="rounded-md p-1.5 hover:bg-red-50" 
                                    title={isRtl ? 'حذف نقطة التلامس' : 'Delete touchpoint'}
                                >
                                    <MdDeleteSweep className="w-5 h-5 text-red-600" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const TouchpointForm = ({
    currentLang,
    title,
    onTitleChange,
    onAdd,
    curvePos,
    svgRef,
    bezier,
    startDrag
}) => {
    const isRtl = currentLang === 'ar';

    return (
        <div className="mt-4 mb-4 p-6 from-blue-50 to-indigo-50 rounded-xl border border-gray-200 bg-gray-50 shadow-lg" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    {isRtl ? 'إضافة نقطة تلامس جديدة' : `Add New Touchpoint (${currentLang.toUpperCase()})`}
                </h4>
            </div>
            <div className="flex gap-4 items-center w-full mb-6">
                <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {isRtl ? 'عنوان نقطة التلامس' : 'Touchpoint Title'}
                    </label>
                    <input
                        type="text"
                        placeholder={isRtl ? 'أدخل عنوان نقطة التلامس' : `Enter touchpoint title (${currentLang.toUpperCase()})`}
                        value={title}
                        onChange={onTitleChange}
                        className="w-full px-4 py-3 text-sm text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 shadow-sm hover:border-gray-400"
                    />
                </div>
                <div className="flex items-end mt-6">
                    <button
                        type="button"
                        onClick={onAdd}
                        disabled={!title?.trim()}
                    >
                        <IoIosAddCircleOutline className="w-5 h-5 text-blue-600" />
                    </button>
                </div>
            </div>
            <CurvePositionSelector 
                currentLang={currentLang}
                curvePos={curvePos}
                svgRef={svgRef}
                bezier={bezier}
                startDrag={startDrag}
            />
        </div>
    );
};

export const CurvePositionSelector = ({ currentLang, curvePos, svgRef, bezier, startDrag }) => {
    const isRtl = currentLang === 'ar';

    return (
        <div className="w-full bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between mb-3" dir={isRtl ? 'rtl' : 'ltr'}>
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {isRtl ? 'موضع نقطة التلامس على المنحنى' : 'Touchpoint Position on Curve'}
                </label>
                <span className="text-xs font-mono bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold">
                    {curvePos}
                </span>
            </div>
            <div className="w-full flex flex-col items-center py-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border-2 border-dashed border-gray-300">
                <svg
                    ref={svgRef}
                    width="220"
                    height="90"
                    viewBox="0 0 200 90"
                    style={{ background: 'transparent' }}
                    className="drop-shadow-sm"
                >
                    <defs>
                        <linearGradient id="curveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" style={{ stopColor: '#c9996d', stopOpacity: 1 }} />
                            <stop offset="100%" style={{ stopColor: '#8b6914', stopOpacity: 1 }} />
                        </linearGradient>
                    </defs>
                    <path
                        d="M 10 70 Q 100 -30 190 70"
                        stroke="url(#curveGradient)"
                        strokeWidth="3.5"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <circle
                        cx={bezier(curvePos, 10, 100, 190)}
                        cy={bezier(curvePos, 70, -30, 70)}
                        r="8"
                        fill="#fff"
                        stroke="#1192f4"
                        strokeWidth="3"
                        style={{ cursor: 'grab', filter: 'drop-shadow(0 2px 4px rgba(17, 146, 244, 0.3))' }}
                        onMouseDown={startDrag}
                        className="hover:stroke-blue-600 transition-all"
                    />
                    <circle
                        cx={bezier(curvePos, 10, 100, 190)}
                        cy={bezier(curvePos, 70, -30, 70)}
                        r="12"
                        fill="none"
                        stroke="#1192f4"
                        strokeWidth="1.5"
                        strokeDasharray="4,4"
                        opacity="0.3"
                        style={{ pointerEvents: 'none' }}
                    />
                </svg>
                <p className="text-xs text-gray-500 mt-2 italic">
                    {isRtl ? 'اسحب الدائرة لتغيير الموضع' : 'Drag the circle to adjust position'}
                </p>
            </div>
        </div>
    );
};

export const EditTouchpointForm = ({
    currentLang,
    title,
    onTitleChange,
    onSave,
    curvePos,
    svgRef,
    bezier,
    startDrag
}) => {
    const isRtl = currentLang === 'ar';

    return (
        <div className="mb-4 mt-4 p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-200 shadow-lg" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <CiEdit className="w-5 h-5 text-purple-600" />
                    {isRtl ? "تعديل نقطة التلامس" : `Edit Touchpoint (${currentLang.toUpperCase()})`}
                </h4>
            </div>
            <div className="flex gap-4 items-center w-full mb-6">
                <div className="flex-1" dir={isRtl ? 'rtl' : 'ltr'}>
                    <label className="block text-xs font-medium text-gray-600 mb-2">
                        {isRtl ? 'عنوان نقطة التلامس' : 'Touchpoint Title'}
                    </label>
                    <input
                        type="text"
                        placeholder={isRtl ? 'أدخل عنوان نقطة التلامس' : `Enter touchpoint title (${currentLang.toUpperCase()})`}
                        value={title}
                        onChange={onTitleChange}
                        className="w-full px-4 py-3 text-sm text-gray-900 bg-white border-2 border-gray-300 rounded-lg focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-200 transition-all duration-200 shadow-sm hover:border-gray-400"
                    />
                </div>
                <div className="flex items-end mt-6">
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={!title?.trim()}
                    >
                        <IoIosAddCircleOutline className="w-5 h-5 text-blue-600" />
                    </button>
                </div>
            </div>
            <CurvePositionSelector 
                currentLang={currentLang}
                curvePos={curvePos}
                svgRef={svgRef}
                bezier={bezier}
                startDrag={startDrag}
            />
        </div>
    );
};

export const FormActions = ({
    currentLangIndex,
    onPrev,
    onNext,
    onTranslate,
    isTranslating,
    onSubmit,
    isSubmitDisabled,
    submitLabel,
    showTranslate
}) => (
    <div className="flex items-center justify-between gap-3 mt-4">
        <div className="flex gap-2">
            <button 
                type="button" 
                onClick={onPrev} 
                disabled={currentLangIndex === 0} 
                className="px-3 py-1 rounded-md border text-sm disabled:opacity-50"
            >
                Back
            </button>
            <button 
                type="button" 
                onClick={onNext} 
                disabled={currentLangIndex === 5} 
                className="px-3 py-1 rounded-md border text-sm disabled:opacity-50"
            >
                Next
            </button>
            {showTranslate && (
                <button 
                    type="button" 
                    onClick={onTranslate} 
                    disabled={isTranslating} 
                    className="px-3 py-1 rounded-md border text-sm disabled:opacity-50 flex items-center gap-2"
                >
                    {isTranslating && (
                        <span className="inline-block h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    )}
                    <span>Translate</span>
                </button>
            )}
        </div>
        <button
            className="px-3 py-1 rounded-md border text-sm bg-blue-600 text-white border-blue-600 disabled:opacity-50"
            type="submit"
            disabled={isSubmitDisabled}
        >
            {submitLabel}
        </button>
    </div>
);